/**
 * The GTM call sites that are not tied to one section, delegated from the document.
 *
 * Per-page events live where the data is — sections/main-product-kit.liquid fires
 * view_album_detail_shop / click_add_to_cart / click_buy_now because only Liquid there knows
 * the album_id metafield, the variant price and the product title, and
 * sections/main-search.liquid fires view_search_results_shop for the same reason. What is
 * left is a click or an input somewhere in the page, and that is this file.
 *
 * Reference points in shopify-en-store-front are named per binding below.
 */
(function () {
  const CHECKOUT_BUTTON_SELECTOR = '[name="checkout"]';
  const IDENTITY_WAIT_MS = 1500;

  const analytics = () => window.KitAnalytics;
  const template = () => document.querySelector('main[data-template]')?.dataset.template || '';

  /* ---- Cart identity ----------------------------------------------------- */

  /**
   * app/components/AddToCartButton.tsx:47 rides the identity along with its LinesAdd
   * mutation. This theme adds through /cart/add.js, which takes line-item `properties` but
   * not cart `attributes`, so the write is a second call made once the cart has updated —
   * which also covers quick-add on collection cards for free.
   */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      analytics()?.syncCart();
    });
  }

  /* ---- view_cart --------------------------------------------------------- */

  // app/components/Cart.tsx:51 fires it when the cart component mounts. On the cart page
  // that is the page load; the drawer case is in assets/script-cart-drawer.js, where open()
  // lives, because both render the same component in the reference.
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      if (template() === 'cart') window.GTM?.viewCart();
    },
    { once: true }
  );

  /* ---- view_checkout + the last identity write --------------------------- */

  /**
   * app/components/Cart.tsx:220 fires view_checkout on this button, and Cart.tsx:231 makes
   * the button itself an AttributesUpdateInput CartForm: it writes the identity onto the
   * cart and *then* redirects. The interception below is that design, not an embellishment
   * — it is the last chance to attach a session id, and a shopper who logged in after
   * adding to the cart gets their _user_id corrected here.
   *
   * Delegated in the bubble phase so it works for the cart page and the drawer alike,
   * including a drawer re-rendered after add-to-cart. `submitting` guards the re-entry: the
   * submit is replayed once the cart write settles, and that replay comes back through here.
   *
   * Not covered: the dynamic checkout buttons (Shop Pay and friends) rendered by
   * content_for_additional_checkout_buttons. They post straight to their own flow with no
   * interceptable submit, and the reference has no equivalent of them either — an order
   * placed through one carries whatever identity add-to-cart already put on the cart.
   */
  let submitting = false;

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(CHECKOUT_BUTTON_SELECTOR);
    if (!button || button.disabled) return;

    window.GTM?.viewCheckout();

    if (submitting) return;

    const form = button.form || button.closest('form');
    const helper = analytics();
    if (!form || !helper) return;

    event.preventDefault();
    button.setAttribute('aria-disabled', 'true');

    helper
      .whenIdentified(IDENTITY_WAIT_MS)
      .then(() => helper.syncCart())
      .catch((error) => {
        console.error('[analytics] checkout identity write failed; continuing to checkout.', error);
      })
      .then(() => {
        submitting = true;
        button.removeAttribute('aria-disabled');
        // requestSubmit keeps the button as the submitter, which the cart form relies on to
        // tell checkout from an update. Older Safari lacks it, hence the fallback.
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(button);
        } else {
          const proxy = document.createElement('input');
          proxy.type = 'hidden';
          proxy.name = button.name;
          proxy.value = button.value || '';
          form.appendChild(proxy);
          form.submit();
        }
      });
  });

  /* ---- GNB icons --------------------------------------------------------- */

  /**
   * click_search — app/components/PageLayout/index.tsx:270 and :365, the mobile and desktop
   * search entries. Both GNBs here link straight to the search page and share the class, so
   * one selector covers the pair (snippets/gnb.liquid, snippets/gnb-mobile.liquid).
   *
   * click_board_icon — PageLayout/index.tsx:360, which fires it when the help-centre menu is
   * toggled. `gnb__cs-trigger` is that menu's trigger in snippets/gnb.liquid. It fires on
   * close as well as open, matching the reference's toggle handler.
   *
   * click_shop — PageLayout/index.tsx:427, the "Shop" entry in the cross-service nav that
   * also holds Home, Artists and Fans. Desktop only, as in the reference: the mobile drawer
   * renders its own rows from the same linklist and is deliberately left unhooked.
   * `[data-gnb-shop-link]` is set in snippets/gnb.liquid on the item whose handle is `shop`.
   */
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target.closest) return;

    if (target.closest('.gnb__icon-btn--search')) window.GTM?.clickSearch();
    if (target.closest('.gnb__cs-trigger')) window.GTM?.clickBoardIcon();
    if (target.closest('[data-gnb-shop-link]')) window.GTM?.clickShop();
  });

  /* ---- click_shop_banner ------------------------------------------------- */

  /**
   * app/components/CustomSection.tsx:64 sends the banner's own URL. Here the banner is the
   * slideshow section (templates/index.json → home-banner-slideshow), which links from two
   * places per slide: the image itself and the text-box button. The slider dots
   * (`slider-counter__link`) are navigation, not banners, and are deliberately not matched.
   */
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.(
      '.slideshow__media-link[href], .slideshow__slide .banner__buttons a[href]'
    );
    if (!link) return;
    window.GTM?.clickShopBanner({ banner_link: link.getAttribute('href') });
  });

  /* ---- search_product ---------------------------------------------------- */

  /**
   * One event per search, carrying what was actually typed.
   *
   * app/routes/($locale).search.tsx:125 fires the moment the field goes from empty to
   * non-empty and re-arms when it is emptied — `isSearchStartTracked` there, `searchArmed`
   * here. That timing works only because the reference sends no payload: firing on the first
   * keystroke would put a single character in `search_keyword`.
   *
   * So the arming is kept — still one event per empty-to-typed cycle, per page load — but the
   * send waits for the term to settle. Whichever comes first wins: the typing pause, or the
   * search form being submitted. A shopper who types and immediately hits Enter is covered by
   * the second; one who types and clicks a predictive result is covered by the first.
   *
   * assets/script-main-search.js mirrors the typed value into the other search inputs by
   * assigning `.value` directly, which raises no input event, so a keystroke is seen once.
   */
  const SEARCH_SETTLE_MS = 700;

  let searchArmed = true;
  let searchTimer = null;
  let searchTerm = '';

  const sendSearchProduct = () => {
    if (!searchArmed || !searchTerm) return;
    searchArmed = false;
    clearTimeout(searchTimer);
    window.GTM?.searchProduct({ search_keyword: searchTerm });
  };

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!input.matches?.('input[type="search"]')) return;

    searchTerm = (input.value || '').trim();

    if (searchTerm.length === 0) {
      // Emptying the field starts a new search, so re-arm and drop any pending send.
      clearTimeout(searchTimer);
      searchArmed = true;
      return;
    }

    if (!searchArmed) return;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(sendSearchProduct, SEARCH_SETTLE_MS);
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.querySelector?.('input[type="search"]')) return;
    sendSearchProduct();
  });

  /* ---- sort_collection / sort_search_result ------------------------------ */

  /**
   * app/components/SortFilter.tsx:100 fires sort_collection and
   * app/components/SortFilterProduct.tsx:94 fires sort_search_result — two components in the
   * reference, one <sort-dropdown> here (snippets/sort-dropdown.liquid), so the page decides
   * which name is used.
   *
   * The reference's `sort_type` is a hardcoded English label ('Price : Low to High'), stable
   * across UI languages because it never passes through t(). Its equivalent here is the
   * Shopify sort key from data-value, not the option's rendered text: this storefront is
   * multi-language, and the label would split one sort order across ko/ja/en.
   *
   * The home page also renders a <sort-dropdown> (sections/featured-collection.liquid). The
   * reference fires nothing for it, and the template check leaves it alone.
   */
  document.addEventListener('click', (event) => {
    const option = event.target.closest?.('sort-dropdown .dropdown-menu__option[data-value]');
    if (!option) return;

    const payload = { sort_type: option.dataset.value };
    if (template() === 'search') window.GTM?.sortSearchResult(payload);
    else if (template() === 'collection') window.GTM?.sortCollection(payload);
  });

  /* ---- click_page_number ------------------------------------------------- */

  /**
   * app/components/CollectionPagination/index.tsx:104 and :120 send page_location
   * 'category', and app/components/SearchPagination.tsx:37 sends 'search'; click_type
   * separates the arrows from the numbered pages. snippets/pagination.liquid marks the
   * arrows with `pagination__item--arrow` and renders disabled arrows as <span>, so only
   * real links match. Other paginated templates (the blog boards) have no reference event
   * and are left out rather than given an invented page_location.
   */
  const PAGE_LOCATIONS = { collection: 'category', search: 'search' };

  document.addEventListener('click', (event) => {
    const item = event.target.closest?.('a.pagination__item');
    if (!item) return;

    const pageLocation = PAGE_LOCATIONS[template()];
    if (!pageLocation) return;

    window.GTM?.clickPageNumber({
      page_location: pageLocation,
      click_type: item.classList.contains('pagination__item--arrow') ? 'arrow' : 'number',
    });
  });
})();
